<template>
    <v-card-text class="pa-6">

        <!-- API Abilitata -->
        <div v-if="apiKey" class="section-container">
            <div class="section-header mb-4">
                <v-icon color="success" size="20">mdi-api</v-icon>
                <h3 class="section-title">API Key Attiva</h3>
                <v-chip color="success" size="x-small" class="ml-2">Attiva</v-chip>
            </div>

            <v-alert type="success" variant="tonal" class="mb-4" density="compact">
                Le API sono abilitate. Utilizza la chiave qui sotto per integrare i nostri servizi.
            </v-alert>

            <!-- API Key Display -->
            <div class="api-key-container mb-6">
                <v-text-field label="API Key" :type="showApi ? 'text' : 'password'" :model-value="apiKey" readonly
                    variant="outlined" density="comfortable" bg-color="grey-lighten-4" class="api-key-field">
                    <template #prepend-inner>
                        <v-icon color="primary">mdi-key</v-icon>
                    </template>

                    <template #append-inner>
                        <v-btn icon variant="text" size="small" @click="showApi = !showApi">
                            <v-icon size="20">
                                {{ showApi ? 'mdi-eye-off' : 'mdi-eye' }}
                            </v-icon>
                        </v-btn>

                        <v-btn icon variant="text" size="small" @click="copiaApi">
                            <v-icon size="20">mdi-content-copy</v-icon>
                        </v-btn>
                    </template>
                </v-text-field>
            </div>

            <!-- Azioni -->
            <div class="actions-container">
                <v-card variant="outlined" class="action-card">
                    <v-card-text>
                        <div class="d-flex align-center mb-3">
                            <v-icon color="warning" class="mr-2">mdi-refresh</v-icon>
                            <div>
                                <div class="text-subtitle-2">Rigenera API Key</div>
                                <div class="text-caption text-grey-darken-1">Invalida la chiave attuale e ne genera una
                                    nuova</div>
                            </div>
                        </div>
                        <v-btn variant="tonal" color="warning" :loading="loadingRigenera" @click="rigeneraApi"
                            size="small" block>
                            Rigenera
                        </v-btn>
                    </v-card-text>
                </v-card>

                <v-card variant="outlined" class="action-card">
                    <v-card-text>
                        <div class="d-flex align-center mb-3">
                            <v-icon color="error" class="mr-2">mdi-cancel</v-icon>
                            <div>
                                <div class="text-subtitle-2">Disabilita API</div>
                                <div class="text-caption text-grey-darken-1">Revoca l'accesso alle API per questo
                                    account</div>
                            </div>
                        </div>
                        <v-btn variant="tonal" color="error" :loading="loadingToggleApi" @click="disabilitaApi"
                            size="small" block>
                            Disabilita
                        </v-btn>
                    </v-card-text>
                </v-card>
            </div>

            <!-- Documentazione -->
            <v-divider class="my-6" />

            <div class="documentation-section">
                <div class="d-flex align-center mb-3">
                    <v-icon color="primary" class="mr-2">mdi-file-document</v-icon>
                    <h4 class="text-subtitle-1">Documentazione API</h4>
                </div>
                <p class="text-body-2 text-grey-darken-1 mb-3">
                    Consulta la documentazione completa per integrare le nostre API nella tua applicazione.
                </p>
                <v-btn variant="outlined" color="primary" prepend-icon="mdi-open-in-new" size="small"
                    @click="apriDoc()">
                    Vai alla documentazione
                </v-btn>
            </div>
        </div>


        <!-- API Disabilitata -->
        <div v-else class="api-disabled-container">
            <v-card variant="outlined" class="text-center pa-8">
                <v-icon size="64" color="grey-lighten-1" class="mb-4">
                    mdi-api-off
                </v-icon>

                <h3 class="text-h6 mb-2">API non abilitate</h3>

                <p class="text-body-2 text-grey-darken-1 mb-6">
                    Le API non sono attualmente abilitate per questo account.<br>
                    Abilita le API per ottenere una chiave e iniziare l'integrazione.
                </p>

                <v-btn color="primary" prepend-icon="mdi-api" :loading="loadingToggleApi" @click="abilitaApi"
                    size="large" variant="flat">
                    Abilita API
                </v-btn>

                <v-divider class="my-6" />

                <!-- Vantaggi -->
                <div class="benefits-section text-left">
                    <h4 class="text-subtitle-1 mb-3">Vantaggi dell'abilitazione API</h4>
                    <v-list density="compact" bg-color="transparent">
                        <v-list-item prepend-icon="mdi-check-circle" class="px-0">
                            <v-list-item-title class="text-body-2">Integrazione con le tue
                                applicazioni</v-list-item-title>
                        </v-list-item>
                        <v-list-item prepend-icon="mdi-check-circle" class="px-0">
                            <v-list-item-title class="text-body-2">Accesso programmatico ai servizi</v-list-item-title>
                        </v-list-item>
                        <v-list-item prepend-icon="mdi-check-circle" class="px-0">
                            <v-list-item-title class="text-body-2">Automazione dei processi</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </div>
            </v-card>
        </div>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" :timeout="3000">
            {{ snackbar.text }}
        </v-snackbar>

    </v-card-text>
</template>

<script>
import { rigenera_api_key, toggle_api_key } from "@/api/api.profilo"

export default {
    name: "TabApi",

    props: {
        apiKey: {
            type: String,
            default: null
        }
    },

    emits: ['reload-data', 'api-disabled', 'api-regenerated'],

    data() {
        return {
            showApi: false,
            loadingRigenera: false,
            loadingToggleApi: false,
            snackbar: {
                show: false,
                text: "",
                color: "success"
            }
        }
    },

    methods: {
        notify(text, color = "success") {
            this.snackbar = { show: true, text, color }
        },

        copiaApi() {
            navigator.clipboard.writeText(this.apiKey)
            this.notify("API Key copiata negli appunti.")
        },

        async abilitaApi() {
            this.loadingToggleApi = true
            try {
                await toggle_api_key(true)
                // Ricarica i dati dal parent
                this.$emit('reload-data')
                this.notify("API abilitate.")
            } catch {
                this.notify("Errore durante l'abilitazione delle API.", "error")
            } finally {
                this.loadingToggleApi = false
            }
        },

        async disabilitaApi() {
            if (!confirm('Sei sicuro di voler revocare l\'accesso alle API? Tutte le applicazioni che utilizzano questa chiave smetteranno di funzionare.')) return;
            this.loadingToggleApi = true
            try {
                await toggle_api_key(false)
                // Notifica il parent che l'API key è stata disabilitata
                this.$emit('api-disabled')
                this.notify("API disabilitate.")
            } catch {
                this.notify("Errore durante la disabilitazione delle API.", "error")
            } finally {
                this.loadingToggleApi = false
            }
        },

        async rigeneraApi() {
            this.loadingRigenera = true
            try {
                const res = await rigenera_api_key()
                // Emetti la nuova API key al parent
                this.$emit('api-regenerated', res.data.apiKey)
                this.notify("API Key rigenerata.")
            } catch {
                this.notify("Errore durante la rigenerazione.", "error")
            } finally {
                this.loadingRigenera = false
            }
        },

        apriDoc() {
            window.open('https://docs.rilievimisure.it', '_blank')
        }
    }
}
</script>

<style scoped>
.section-container {
    background-color: #fafafa;
    border-radius: 8px;
    padding: 20px;
}

.section-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.api-key-container {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
}

.actions-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
}

.action-card {
    transition: all 0.2s;
}

.action-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.api-disabled-container {
    max-width: 600px;
    margin: 0 auto;
}

.documentation-section {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
}

.benefits-section {
    margin-top: 20px;
}
</style>

<style scoped>
.section-container {
    background-color: #fafafa;
    border-radius: 8px;
    padding: 20px;
}

.section-header {
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.api-key-container {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
}

.actions-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 16px;
}

.action-card {
    transition: all 0.2s;
}

.action-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.api-disabled-container {
    max-width: 600px;
    margin: 0 auto;
}

.documentation-section {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
}

.benefits-section {
    margin-top: 20px;
}
</style>